/**
 * Kundenstammverwaltung.
 *
 * Eine Liste von Karten, eine Zeile je Kunde. Zwei Klickziele, klar getrennt:
 * Der Name führt in die Stammdaten – so wie man es überall erwartet –, der Rest
 * der Zeile klappt sie auf und zeigt die Vorgänge dieses Kunden. Stift, Menü
 * und ein eigener „Bearbeiten"-Knopf sind entfallen; der Name ist der Knopf.
 *
 * Was diese Seite beantworten soll: Wen habe ich, wen kann ich nicht erreichen,
 * und was läuft gerade. Deshalb tragen fehlende Kontaktdaten einen eigenen
 * Zustand statt zweier Gedankenstriche, und deshalb steht rechts nicht die
 * Kundennummer, sondern was zuletzt geschehen ist.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiFehler } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { useKora } from '../zustand';
import { KundenModal } from '../modals/KundenModal';
import { SevdeskKontaktModal } from '../modals/SevdeskKontaktModal';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { IconGebaeude, IconMuell, IconPerson, IconRechnung, IconSuche } from '../komponenten/Icons';
import { dat, norm } from '../hilfen/format';
import type { KundenUebersicht, KundenZeile } from '../typen';
import './kunden.css';
import { Menueknopf } from '../komponenten/Shell';

/** Die vier Gruppen der Filterleiste. Sie teilen den Bestand auf, ohne Überschneidung. */
type Gruppe = 'alle' | 'person' | 'organisation' | 'lieferant';

/** Wonach sortiert wird – drei Knöpfe statt eines Dreiecks im Spaltenkopf. */
type Sortierung = 'zuletzt' | 'name' | 'nummer';

export function Kundenstamm() {
  const navigate = useNavigate();
  const { nutzer, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const [suche, setSuche] = useState('');
  const [gruppe, setGruppe] = useState<Gruppe>('alle');
  const [nurOhneKontakt, setNurOhneKontakt] = useState(false);
  /*
   * Kunden ohne sevDesk-Kontakt sind seit v12 ein Filter, kein eigener
   * Arbeitsbereich: Der Menuepunkt „sevDesk-Abgleich" ist entfallen, und das
   * Admin Center verlinkt die Zahl hierher. `?ohneSevdesk=1` schaltet den
   * Filter beim Oeffnen ein – sonst landet man auf einer vollen Liste und
   * sucht die Kunden, die gemeint waren.
   */
  const ort = useLocation();
  const [nurOhneSevdesk, setNurOhneSevdesk] = useState(
    () => new URLSearchParams(ort.search).get('ohneSevdesk') === '1',
  );
  /*
   * Auch dann einschalten, wenn die Maske schon offen war: Wer im Admin
   * Center auf die Zahl klickt, während der Kundenstamm dahinter liegt,
   * bekommt sonst dieselbe volle Liste wie vorher.
   */
  useEffect(() => {
    if (new URLSearchParams(ort.search).get('ohneSevdesk') === '1') setNurOhneSevdesk(true);
  }, [ort.search]);
  const [sortierung, setSortierung] = useState<Sortierung>('zuletzt');
  const [offenerKunde, setOffenerKunde] = useState<number | null>(null);
  const [modal, setModal] = useState<{ kundeId: number | null } | null>(null);
  const [loeschen, setLoeschen] = useState<{ id: number; name: string } | null>(null);
  const [loeschFehler, setLoeschFehler] = useState('');
  /** Kunde, dessen sevDesk-Kontakt gerade zugeordnet oder geändert wird. */
  const [sevdeskModal, setSevdeskModal] = useState<KundenZeile | null>(null);

  const abfrage = useQuery({
    queryKey: ['kunden-uebersicht'],
    queryFn: () => api.hole<KundenUebersicht>('/kunden/uebersicht'),
  });

  /*
   * Löschen ist der Administration vorbehalten und nur ohne Vorgänge möglich.
   * Die Regel prüft der Server; hier steht der Knopf nur dort, wo er auch
   * funktioniert. Ein Knopf, der verlässlich eine Fehlermeldung liefert, ist
   * schlimmer als keiner.
   */
  const loeschung = useMutation({
    mutationFn: (id: number) => api.loesche<{ toast?: string }>(`/kunden/${id}`),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['kunden-uebersicht'] });
      void queryClient.invalidateQueries({ queryKey: ['kunden'] });
      toastZeigen(a?.toast ?? 'Kunde gelöscht.');
      setLoeschen(null);
    },
    onError: (fehler) =>
      setLoeschFehler(
        fehler instanceof ApiFehler ? fehler.message : 'Der Kunde konnte nicht gelöscht werden.',
      ),
  });

  const d = abfrage.data;
  const alle = useMemo(() => d?.kunden ?? [], [d]);
  const q = norm(suche);

  const gefiltert = useMemo(() => {
    const istLieferant = (k: KundenZeile) => k.rolle === 'Lieferant';
    return alle
      .filter((k) => {
        if (gruppe === 'alle') return true;
        if (gruppe === 'lieferant') return istLieferant(k);
        return !istLieferant(k) && k.typ === gruppe;
      })
      .filter((k) => !nurOhneKontakt || k.kontaktFehlt)
      .filter((k) => !nurOhneSevdesk || (!istLieferant(k) && !k.sevdeskId))
      /*
       * Die Suche geht über alles, was auf der Zeile steht – und über die
       * Kundennummer und die Straße, die dort nicht immer stehen. Wer eine
       * Nummer im Kopf hat, soll sie eintippen können.
       */
      .filter(
        (k) =>
          !q ||
          [k.name, k.ort.text, k.mail, k.telefon, k.adresse, k.kundenNr].some((x) =>
            norm(x).includes(q),
          ),
      );
  }, [alle, gruppe, nurOhneKontakt, nurOhneSevdesk, q]);

  const sortiert = useMemo(() => {
    const liste = [...gefiltert];
    if (sortierung === 'name') return liste.sort((a, b) => norm(a.name).localeCompare(norm(b.name)));
    if (sortierung === 'nummer') {
      return liste.sort(
        (a, b) => Number(a.kundenNr.replace(/\D/g, '')) - Number(b.kundenNr.replace(/\D/g, '')),
      );
    }
    /* Zuletzt aktiv: Wer gerade in Bewegung ist, steht oben. */
    return liste.sort((a, b) => (a.zuletzt.datum < b.zuletzt.datum ? 1 : -1));
  }, [gefiltert, sortierung]);

  const z = d?.zaehler;

  const gruppen: { key: Gruppe; label: string; zahl: number }[] = [
    { key: 'alle', label: 'Alle', zahl: z?.alle ?? 0 },
    { key: 'person', label: 'Privatpersonen', zahl: z?.person ?? 0 },
    { key: 'organisation', label: 'Organisationen', zahl: z?.organisation ?? 0 },
    { key: 'lieferant', label: 'Lieferanten', zahl: z?.lieferant ?? 0 },
  ];

  const sortierungen: { key: Sortierung; label: string }[] = [
    { key: 'zuletzt', label: 'Zuletzt aktiv' },
    { key: 'name', label: 'Name A–Z' },
    { key: 'nummer', label: 'Kundennummer' },
  ];

  return (
    <div className="kunden-seite">
      <div className="kunden-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
          <div className="kunden-augenbraue">Stammdaten</div>
          <h1 className="kunden-titel">Kunden</h1>
          <div className="kunden-unterzeile">
            {z
              ? `${z.alle} ${z.alle === 1 ? 'Kunde' : 'Kunden'} · ${z.ohneKontakt} ohne Kontaktdaten` +
                (d?.darfAngebote ? ' · Angebote je Kunde gebündelt' : '')
              : 'Wird geladen …'}
          </div>
        </div>

        </div>
        <div className="kunden-kopf-rechts eh-kopf-rechts">
          <Nachrichtenknopf />
          {/*
            Kunden ohne sevDesk-Kontakt – der übernommene Bestand. Die Zahl ist
            eine Aufgabe: Solange sie über null liegt, findet der Rechnungs-
            abgleich diese Kunden nur über die Angebotsnummer.
          */}
          {!!z?.ohneSevdesk && (
            <button
              type="button"
              className={`kunden-abgleich ${nurOhneSevdesk ? 'aktiv' : ''}`}
              title="Nur Kunden ohne sevDesk-Kontakt zeigen – zuordnen über das Stiftsymbol der Zeile"
              aria-pressed={nurOhneSevdesk}
              onClick={() => setNurOhneSevdesk(!nurOhneSevdesk)}
            >
              {z.ohneSevdesk} ohne sevDesk-Kontakt
            </button>
          )}
          <label className="kunden-suchfeld">
            <IconSuche groesse={16} />
            <input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Name, Ort, Mail, Telefon, Kundennummer, Straße"
              aria-label="Kunden durchsuchen"
            />
          </label>
          <button type="button" className="kunden-neu" onClick={() => setModal({ kundeId: null })}>
            Neuen Kunden anlegen <span aria-hidden="true">+</span>
          </button>
        </div>
      </div>

      <div className="kunden-leiste">
        {/*
          Ein Segment mit Anzahl je Gruppe. Es ersetzt die alte Reiterzeile samt
          Symbol-Legende „◉ Privatperson ▦ Organisation" – die stand rechts oben
          und wurde nicht gelesen, weil die Symbole ohnehin niemand entzifferte.
        */}
        <div className="kunden-gruppen">
          {gruppen.map((g) => (
            <button
              key={g.key}
              type="button"
              className={`kunden-gruppe ${gruppe === g.key ? 'aktiv' : ''}`}
              onClick={() => setGruppe(g.key)}
            >
              {g.label} <span className="kunden-gruppe-zahl">{g.zahl}</span>
            </button>
          ))}
        </div>

        <div className="kunden-werkzeuge">
          {/*
            Die Liste zum Nachtelefonieren. Wer nicht erreichbar ist, ist ein
            Vertriebsproblem – dafür braucht es einen Filter, nicht das Absuchen
            von achtzig Zeilen nach Gedankenstrichen.
          */}
          {!!z?.ohneKontakt && (
            <button
              type="button"
              className={`kunden-mangelfilter ${nurOhneKontakt ? 'aktiv' : ''}`}
              onClick={() => setNurOhneKontakt(!nurOhneKontakt)}
              aria-pressed={nurOhneKontakt}
            >
              <span className="kunden-mangelpunkt" />
              Kontaktdaten fehlen · {z.ohneKontakt}
            </button>
          )}

          <div className="kunden-sortierung">
            {sortierungen.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`kunden-sortknopf ${sortierung === s.key ? 'aktiv' : ''}`}
                onClick={() => setSortierung(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/*
        Kopf und Zeilen stehen im selben Container (v13, 5.4): Eine
        Container-Query wirkt nur auf Nachfahren, und ein Tabellenkopf, der
        die Spalten der Zeilen darunter nicht mehr trifft, ist schlimmer als
        gar keiner.
      */}
      <div className="kunden-tabelle">
      <div className="kunden-tabellenkopf">
        {/* Erste Zelle bleibt leer – dort steht in der Zeile die Typkachel. */}
        <div />
        <div>Kunde</div>
        <div>Ort</div>
        <div>Kontakt</div>
        <div>Angebote</div>
        <div>Zuletzt</div>
        <div />
      </div>

      <div className="kunden-liste">
        {sortiert.map((k) => (
          <Kundenzeile
            key={k.id}
            kunde={k}
            offen={offenerKunde === k.id}
            darfAngebote={!!d?.darfAngebote}
            istAdmin={!!nutzer?.istAdmin}
            onUmschalten={() => setOffenerKunde(offenerKunde === k.id ? null : k.id)}
            onBearbeiten={() => setModal({ kundeId: k.id })}
            onAngebot={(id) => navigate(`/angebot/${id}`, { state: { herkunft: 'kunden' } })}
            onNeuesAngebot={() => navigate('/neu', { state: { kundeId: k.id } })}
            onSevdesk={() => setSevdeskModal(k)}
            onLoeschen={() => {
              setLoeschFehler('');
              setLoeschen({ id: k.id, name: k.name });
            }}
          />
        ))}

        {abfrage.isLoading && <div className="kunden-leer">Kunden werden geladen …</div>}
        {!abfrage.isLoading && sortiert.length === 0 && (
          <div className="kunden-leer">
            {nurOhneKontakt
              ? 'Kein Kunde ohne Kontaktdaten – erfreulich.'
              : 'Kein Kunde gefunden.'}
          </div>
        )}
      </div>
      </div>

      {modal && (
        <KundenModal
          kundeId={modal.kundeId}
          onSchliessen={() => {
            setModal(null);
            void queryClient.invalidateQueries({ queryKey: ['kunden-uebersicht'] });
          }}
        />
      )}

      {sevdeskModal && (
        <SevdeskKontaktModal
          kunde={sevdeskModal}
          onSchliessen={() => {
            setSevdeskModal(null);
            void queryClient.invalidateQueries({ queryKey: ['kunden-uebersicht'] });
          }}
        />
      )}

      {loeschen && (
        <Modal
          titel="Kunden löschen"
          untertitel={loeschen.name}
          breite={430}
          fest={loeschung.isPending}
          onSchliessen={() => setLoeschen(null)}
          fuss={
            <>
              <KnopfSekundaer onClick={() => setLoeschen(null)} disabled={loeschung.isPending}>
                Abbrechen
              </KnopfSekundaer>
              <KnopfPrimaer
                onClick={() => loeschung.mutate(loeschen.id)}
                disabled={loeschung.isPending}
              >
                {loeschung.isPending ? 'Wird gelöscht …' : 'Endgültig löschen'}
              </KnopfPrimaer>
            </>
          }
        >
          <p className="eh-hinweis" style={{ margin: 0, lineHeight: 1.6 }}>
            Der Kunde wird endgültig entfernt und lässt sich nicht zurückholen. Möglich ist das
            nur, weil ihm kein Angebot zugeordnet ist – der Server prüft das noch einmal.
          </p>
          {loeschFehler && <div className="eh-fehler">{loeschFehler}</div>}
        </Modal>
      )}
    </div>
  );
}

/**
 * Eine Kundenkarte.
 *
 * Der Name ist ein Link in die Stammdaten, der Rest der Zeile klappt auf. Damit
 * beides nebeneinander funktioniert, hält der Name den Klick auf – sonst würde
 * er die Zeile mit aufklappen.
 */
function Kundenzeile({
  kunde,
  offen,
  darfAngebote,
  istAdmin,
  onUmschalten,
  onBearbeiten,
  onAngebot,
  onNeuesAngebot,
  onSevdesk,
  onLoeschen,
}: {
  kunde: KundenZeile;
  offen: boolean;
  darfAngebote: boolean;
  istAdmin: boolean;
  onUmschalten: () => void;
  onBearbeiten: () => void;
  onAngebot: (id: number) => void;
  onNeuesAngebot: () => void;
  /** sevDesk-Kontakt zuordnen, ändern oder lösen. */
  onSevdesk: () => void;
  onLoeschen: () => void;
}) {
  const k = kunde;
  const aufklappbar = darfAngebote;

  return (
    <div className={`kunden-karte ${offen ? 'offen' : ''}`}>
      <div
        className={`kunden-zeile ${aufklappbar ? 'klickbar' : ''}`}
        onClick={aufklappbar ? onUmschalten : undefined}
      >
        {/*
          Die Kachel ist bei beiden Typen dieselbe – nur das Motiv unterscheidet
          sie. Über die Farbe zu trennen hieße, jemanden mit Rot-Grün-Schwäche
          im Regen stehen zu lassen; über die Form trägt es allein.
        */}
        <div className="kunden-typkachel" title={k.typLabel}>
          {k.typ === 'organisation' ? <IconGebaeude groesse={22} /> : <IconPerson groesse={22} />}
          <span className="eh-nur-vorlesen">{k.typLabel}</span>
        </div>

        <div className="kunden-namensblock">
          <button
            type="button"
            className="kunden-name"
            onClick={(e) => {
              e.stopPropagation();
              onBearbeiten();
            }}
          >
            {k.name}
          </button>
          <div className="kunden-zusatz">
            {[k.typLabel, `Nr. ${k.kundenNr}`, k.adresse].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div className={`kunden-ort ${k.ort.zustand}`}>
          <span>{k.ort.text}</span>
          {k.ort.zustand === 'unvollstaendig' && (
            <span className="kunden-ortmangel">Ort unvollständig</span>
          )}
        </div>

        {/*
          Mail und Telefon sind Links, keine abzutippenden Texte. Fehlt beides,
          steht dort ein Zustand und kein Gedankenstrich.
        */}
        <div className="kunden-kontakt" onClick={(e) => e.stopPropagation()}>
          {k.kontaktFehlt ? (
            <span className="kunden-mangelchip">Kontaktdaten fehlen</span>
          ) : (
            <>
              {k.mail && (
                <a className="kunden-kontaktlink" href={`mailto:${k.mail}`}>
                  {k.mail}
                </a>
              )}
              {k.telefon && (
                <a className="kunden-kontaktlink" href={`tel:${k.telefonWahl}`}>
                  {k.telefon}
                </a>
              )}
            </>
          )}
        </div>

        <div className="kunden-angebote">
          {darfAngebote ? (
            <>
              <span className="kunden-anzahl">{k.anzahlText}</span>
              <span className="kunden-gewerke">
                {k.gewerke.map((g) => (
                  <span className="kunden-gewerk" key={g}>
                    {g}
                  </span>
                ))}
              </span>
            </>
          ) : (
            <span className="kunden-gesperrt">—</span>
          )}
        </div>

        <div className="kunden-zuletzt">
          <span className="kunden-zuletzt-datum">{dat(k.zuletzt.datum)}</span>
          <span className="kunden-zuletzt-text">{k.zuletzt.text}</span>
        </div>

        {aufklappbar ? (
          <button
            type="button"
            className="kunden-chevron"
            aria-expanded={offen}
            aria-label={offen ? 'Vorgänge zuklappen' : 'Vorgänge aufklappen'}
            onClick={(e) => {
              e.stopPropagation();
              onUmschalten();
            }}
          >
            <span aria-hidden="true">{offen ? '⌃' : '⌄'}</span>
          </button>
        ) : (
          <span />
        )}
      </div>

      {offen && (
        <div className="kunden-aufklappung">
          <div className="kunden-aufklappung-kopf">
            <span className="kunden-aufklappung-titel">Angebote dieses Kunden</span>
            <button type="button" className="kunden-neuesangebot" onClick={onNeuesAngebot}>
              Neues Angebot für diesen Kunden
            </button>
          </div>

          {k.angebote.length === 0 && (
            <div className="kunden-leer klein">Noch kein Angebot zu diesem Kunden.</div>
          )}

          {k.angebote.map((a) => (
            <button
              key={a.id}
              type="button"
              className="kunden-angebotszeile"
              onClick={() => onAngebot(a.id)}
            >
              <span className="kunden-angebot-kennung">{a.kennung}</span>
              <span className="kunden-angebot-kategorie">{a.kategorie}</span>
              {/*
                Chip statt Ampelpunkt (v11, 10.2 Regel 5): Ein Punkt sagt nur,
                dass etwas ist – der Chip sagt, was. Wiedervorlage ist eine
                Information und keine Warnung, deshalb Info-Slate.
              */}
              <span className="kunden-angebot-status">
                <span className={`eh-status-chip ${a.wiedervorlage ? 'info' : 'ok'}`}>
                  {a.wiedervorlage ? `Wiedervorlage ${dat(a.wiedervorlage)}` : a.status}
                </span>
              </span>
              <span className="kunden-angebot-datum">{dat(a.datum)}</span>
            </button>
          ))}

          {/*
            Absprung in die Buchhaltung. Er steht in der aufgeklappten Zeile
            und nicht in der Liste: Gebraucht wird er, wenn jemand mit diesem
            einen Kunden etwas vorhat – nicht beim Überfliegen von achtzig.
          */}
          {/*
            Ohne Kontakt gibt es keinen Absprung – dann steht hier, was fehlt,
            und der Knopf, es nachzuholen. Der Stift daneben ändert oder löst
            eine bestehende Verknüpfung; jede Änderung landet im Protokoll.
          */}
          <div className="kunden-sevdesk-zeile">
            {k.sevdeskUrl ? (
              <>
                <a className="kunden-sevdesk" href={k.sevdeskUrl} target="_blank" rel="noreferrer">
                  <IconRechnung groesse={14} />
                  In sevDesk öffnen
                  {k.sevdeskNummer ? ' · Nr. ' + k.sevdeskNummer : ''}
                  <span aria-hidden="true">↗</span>
                </a>
                <button
                  type="button"
                  className="kunden-sevdesk-knopf"
                  title="sevDesk-Kontakt ändern oder Verknüpfung lösen"
                  onClick={onSevdesk}
                >
                  ✎ Verknüpfung ändern
                </button>
              </>
            ) : (
              <>
                <span className="kunden-sevdesk-fehlt">Kein sevDesk-Kontakt verknüpft</span>
                <button type="button" className="kunden-sevdesk-knopf" onClick={onSevdesk}>
                  Kontakt zuordnen
                </button>
              </>
            )}
          </div>

          {istAdmin && k.loeschbar && (
            <button type="button" className="kunden-loeschen" onClick={onLoeschen}>
              <IconMuell groesse={14} />
              Kunden löschen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
